import { ArrowLeft, RotateCw, Sparkles } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { QuizCardSurface, usePracticeFlow } from "@/components/QuizCard";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useT } from "@/lib/i18n";
import { usePracticeSession, type QuizPracticeCard } from "@/lib/practice";
import { cn } from "@/lib/utils";

// ─── /grammar/:id/practice ─────────────────────────────────────────
//
// Per-rule drill page. Walks the rule's quiz items in stable
// (insertion) order with no SRS impact — the user can re-drill the
// same rule daily without polluting their schedule.
//
// Distinct from Study.tsx because:
//   - Items come from one rule, not the global queue.
//   - Header is `<QuizHeader>` (rule + mode + N/M) not `<SessionTopBar>`.
//   - Bottom nav is suppressed (full-screen drill, see AppShell).
//   - Submit hits a stateless endpoint; advance is local-list-index,
//     not queue-cache eviction.
//
// State reset on card change handled by `<PracticeCard>`'s key prop.
export function PracticeSessionPage() {
  const { id: ruleId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const t = useT();
  const { data, isPending, isError } = usePracticeSession(ruleId);
  const [index, setIndex] = useState(0);

  if (isPending) {
    return <PracticeSkeleton />;
  }

  if (isError || !data) {
    return (
      <div className="flex min-h-[calc(100dvh-40px)] flex-col items-center justify-center gap-3">
        <div className="text-[14px] text-text-3">{t("practice.load_error")}</div>
        <Button variant="secondary" onClick={() => navigate(-1)}>
          {t("common.back")}
        </Button>
      </div>
    );
  }

  const { rule, items } = data;

  if (items.length === 0) {
    return <PracticeEmpty rule={rule} />;
  }

  const isDone = index >= items.length;
  if (isDone) {
    return <PracticeDone rule={rule} total={items.length} onReset={() => setIndex(0)} />;
  }

  const card = items[index]!;

  return (
    <div className="flex min-h-[calc(100dvh-40px)] flex-col gap-3.5">
      <QuizHeader
        ruleTitle={rule.title}
        level={rule.level}
        mode={card.mode}
        progress={index + 1}
        total={items.length}
        onBack={() => navigate(`/grammar/${ruleId}`)}
      />
      {/* `key` forces a fresh PracticeCard mount per card so local
          flow state (selectedOption / typedInput / result) resets
          cleanly without us threading a reset effect through. */}
      <PracticeCard key={card.quizItemId} card={card} ruleId={ruleId ?? ""} onComplete={() => setIndex((i) => i + 1)} />
    </div>
  );
}

// Wrapping the surface so the per-card `usePracticeFlow` hook lives
// inside a component that remounts via `key` from the parent. Hook
// at parent level can't be remounted that way.
function PracticeCard({
  card,
  ruleId,
  onComplete,
}: {
  card: QuizPracticeCard;
  ruleId: string;
  onComplete: () => void;
}) {
  const flow = usePracticeFlow(card, { ruleId, onComplete });
  return <QuizCardSurface card={card} flow={flow} />;
}

// ─── QuizHeader chrome ─────────────────────────────────────────────
// Per `design/hifi/exam.jsx → QuizHeader`. Back button + rule title
// + (mode · N/M) eyebrow + level chip + thin progress bar at bottom.
function QuizHeader({
  ruleTitle,
  level,
  mode,
  progress,
  total,
  onBack,
}: {
  ruleTitle: string;
  level: string | null;
  mode: "mcq" | "type";
  progress: number;
  total: number;
  onBack: () => void;
}) {
  const t = useT();
  const pct = total > 0 ? (progress / total) * 100 : 0;
  const modeLabel = mode === "mcq" ? t("practice.mode_mcq") : t("practice.mode_type");

  return (
    <div className="-mx-4 border-b border-border">
      <div className="flex items-center gap-2 px-3 pt-1 pb-2">
        <button
          type="button"
          onClick={onBack}
          aria-label={t("common.back")}
          className="-ml-2 flex size-9 items-center justify-center rounded-full text-text-2 transition-colors hover:text-text"
        >
          <ArrowLeft className="size-5" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13px] font-semibold leading-[1.2]">{ruleTitle}</div>
          <div className="mt-0.5 font-mono text-[10px] text-text-3">
            {modeLabel} · {progress}/{total}
          </div>
        </div>
        {level && (
          <span
            className="inline-flex items-center rounded-md px-2 py-0.5 font-mono text-[11px] font-bold tracking-[0.03em]"
            style={{ background: "var(--accent-soft)", color: "var(--accent)" }}
          >
            {level}
          </span>
        )}
      </div>
      {/* Progress bar — flush with the bottom border, no rounding. */}
      <div className="h-[3px] w-full bg-[color:var(--surface-2)]">
        <div
          className="h-full transition-[width] duration-200"
          style={{ width: `${pct}%`, background: "var(--accent)" }}
        />
      </div>
    </div>
  );
}

// ─── States ────────────────────────────────────────────────────────
function PracticeSkeleton() {
  return (
    <div className="flex flex-col gap-3.5">
      <div className="-mx-4 border-b border-border">
        <div className="flex items-center gap-2 px-3 pt-1 pb-2">
          <Skeleton className="size-9 rounded-full" />
          <div className="flex-1">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="mt-1 h-3 w-20" />
          </div>
          <Skeleton className="h-5 w-8" />
        </div>
        <Skeleton className="h-[3px] w-full rounded-none" />
      </div>
      <div className="flashcard flex-1 gap-4">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-3 w-2/3" />
        <div className="flex flex-col gap-2 pt-3">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      </div>
    </div>
  );
}

function PracticeEmpty({ rule }: { rule: { id: string; title: string } }) {
  const t = useT();
  return (
    <div className="flex min-h-[calc(100dvh-40px)] flex-col items-center justify-center gap-4 text-center">
      <div className={cn("text-[18px] font-semibold")}>{t("practice.empty_title")}</div>
      <div className="max-w-[18rem] text-[13px] text-text-3">{t("practice.empty_body", { rule: rule.title })}</div>
      <Button asChild variant="secondary">
        <Link to={`/grammar/${rule.id}`}>{t("common.back")}</Link>
      </Button>
    </div>
  );
}

function PracticeDone({
  rule,
  total,
  onReset,
}: {
  rule: { id: string; title: string };
  total: number;
  onReset: () => void;
}) {
  const t = useT();
  return (
    <div className="flex min-h-[calc(100dvh-40px)] flex-col items-center justify-center gap-4 text-center">
      <div
        className="flex size-16 items-center justify-center rounded-full"
        style={{ background: "var(--good-soft)", color: "var(--good)" }}
      >
        <Sparkles className="size-7" />
      </div>
      <div className="text-[20px] font-bold tracking-[-0.02em]">{t("practice.done_title")}</div>
      <div className="max-w-[18rem] text-[13px] text-text-3">
        {t("practice.done_body", { n: total, rule: rule.title })}
      </div>
      <div className="flex flex-col gap-2 pt-2">
        <Button onClick={onReset} className="gap-2">
          <RotateCw className="size-4" />
          {t("practice.do_again")}
        </Button>
        <Button asChild variant="secondary">
          <Link to={`/grammar/${rule.id}`}>{t("common.back")}</Link>
        </Button>
      </div>
    </div>
  );
}
